// The park behind the round (spec FE-24): a 60 s loop of a Lagos park crowd and traffic, our own
// recording (assets/audio/park-crowd). Full on the road, low under a meeting or the results, gone
// outside a round. It goes through the same master gain as every other sound (output()), so it is
// silent before the first tap, with sound off and while Lagos Life has the game paused. The file is
// fetched only when a round first wants it. Over it plays the synthesised park bed and the siren
// (parkbed.ts, loaded on first use). The play settings' Park sound turns the whole park off.
import crowdUrl from '../assets/audio/park-crowd-loop.mp3';
import { onPause } from './pause';
import { getPrefs, onPrefs } from './prefs';
import { onSound, output } from './sound';

type Bed = typeof import('./parkbed');
let bedMod: Promise<Bed> | null = null;
const bed = (): Promise<Bed> => {
  bedMod ??= import('./parkbed').catch((e) => {
    bedMod = null;
    throw e;
  });
  return bedMod;
};

export type Level = 'off' | 'road' | 'low';
const GAIN: Record<Level, number> = { off: 0, road: 0.32, low: 0.1 };
const RAMP_S = 0.8;

let want: Level = 'off';
let buffer: Promise<AudioBuffer | null> | null = null;
let playing: { src: AudioBufferSourceNode; gain: GainNode } | null = null;

function load(ctx: AudioContext): Promise<AudioBuffer | null> {
  buffer ??= fetch(crowdUrl)
    .then((r) => (r.ok ? r.arrayBuffer() : Promise.reject(new Error(String(r.status)))))
    .then((b) => ctx.decodeAudioData(b))
    .catch(() => {
      buffer = null; // try again next time a round asks
      return null;
    });
  return buffer;
}

function stop(): void {
  const p = playing;
  if (!p) return;
  playing = null;
  const t = p.gain.context.currentTime;
  p.gain.gain.cancelScheduledValues(t);
  p.gain.gain.setValueAtTime(p.gain.gain.value, t);
  p.gain.gain.linearRampToValueAtTime(0, t + RAMP_S);
  p.src.stop(t + RAMP_S + 0.05);
}

/** Bring what plays in line with what the round wants and what output() allows. */
function sync(): void {
  const o = output();
  const level = want;
  if (!o || level === 'off' || getPrefs().parkSound === 'off' || document.hidden) {
    stop();
    if (bedMod) void bedMod.then((m) => m.stopBed());
    return;
  }
  const { ctx, out } = o;
  void bed()
    .then((m) => {
      if (want === level && output()) m.playBed(ctx, out, level);
    })
    .catch(() => {});
  if (playing) {
    const t = ctx.currentTime;
    playing.gain.gain.cancelScheduledValues(t);
    playing.gain.gain.setValueAtTime(playing.gain.gain.value, t);
    playing.gain.gain.linearRampToValueAtTime(GAIN[want], t + RAMP_S);
    return;
  }
  void load(ctx).then((buf) => {
    if (!buf || playing || want === 'off' || !output()) return;
    const src = ctx.createBufferSource();
    src.buffer = buf;
    src.loop = true;
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0, ctx.currentTime);
    gain.gain.linearRampToValueAtTime(GAIN[want], ctx.currentTime + RAMP_S * 2);
    src.connect(gain).connect(out);
    // start somewhere in the loop, so every round doesn't open on the same second (not game state:
    // Math.random is fine here)
    src.start(0, Math.random() * buf.duration);
    playing = { src, gain };
  });
}

/** What the round wants behind it: the road, a quiet park under a meeting, or nothing. */
export function setAmbience(level: Level): void {
  if (level === want) return;
  want = level;
  sync();
}

/**
 * The siren (FE-24): an arrest `dist` world units from this keke, `pan` toward it (-1 left to 1
 * right); null for a wahala, heard park-wide. Gameplay news, so it follows sound on or off only.
 */
export function siren(dist: number | null, pan = 0): void {
  const o = output();
  if (!o) return;
  void bed()
    .then((m) => m.siren(o.ctx, o.out, dist, pan))
    .catch(() => {});
}

onSound(() => sync());
onPrefs(() => sync());
addEventListener('visibilitychange', () => sync());
onPause((p) => {
  if (!p) sync();
});
// the first tap unlocks audio (sound.ts): a round that was already waiting starts then
const firstTap = () =>
  queueMicrotask(() => {
    if (output()) removeEventListener('pointerdown', firstTap, { capture: true });
    sync();
  });
addEventListener('pointerdown', firstTap, { capture: true });
